import { useEffect, useState } from "react"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetInventoryQuery } from "store/api/widgetsApi"
import { useAppDispatch } from "store/hooks"
import { showNewOrderModal } from "store/modalSlise"

import WidgetsLayout from "layouts/WidgetsLayout"
import { BarChart } from "components/common/BarChart"
import { Tooltip } from "components/common/Tooltip"

const SufficientTooltip = styled(Tooltip)`
  div {
    max-width: 350px;
  }
`

const initialState = {
  sufficientDays: 0,
  inventoryDevices: 0,
  orderedDevices: 0,
}

const WidgetContent = styled.div`
  position: relative;
  margin-top: 20px !important;
`

const MainValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px 0 10px;
`

const MainLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`

const ValueWrapper = styled.div`
  margin-top: 4px;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.5;
  color: #0f172a;
`

const ValueNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`

const WidgetFooter = styled.footer`
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
  display: flex;
  justify-content: center;
`

const WidgetFooterItem = styled.div`
  text-align: center;
  border-right: 1px solid #f1f5f9;
  flex: 1;

  &:last-child {
    border: none;
  }
`

const FooterNote = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;

  &::before {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-flex;
    content: "";
    background-color: ${({ $color }) => $color};
  }
`

interface Props {
  updateIndex: number
}

export const WidgetInventory = ({ updateIndex }: Props) => {
  const dispatch = useAppDispatch()
  const [triggerGetInventory, { isFetching }] = useLazyGetInventoryQuery()
  const [widgetData, setWidgetData] = useState(initialState)

  useEffect(() => {
    const fetchData = async () => {
      const res = await triggerGetInventory()
      if (res?.data?.data) {
        const data = res.data.data
        setWidgetData({
          sufficientDays: data.sufficient_days ?? 0,
          inventoryDevices: data.inventory_devices ?? 0,
          orderedDevices: data.ordered_devices ?? 0,
        })
      }
    }

    fetchData()
  }, [updateIndex, triggerGetInventory])

  const onNewClick = () => {
    dispatch(showNewOrderModal(true))
  }

  return (
    <WidgetsLayout title="Inventory" buttonText="New order" onButtonClick={onNewClick} isFetching={isFetching}>
      <WidgetContent>
        <BarChart
          items={[
            { value: widgetData.inventoryDevices, color: "#3B82F6" },
            { value: widgetData.orderedDevices, color: "#BFDBFE" },
          ]}
        />
        <MainValueWrapper>
          <MainLabel>
            <SufficientTooltip id={uuidv4()} text="SUFFICIENT FOR">
              This indicator gauges sufficiency by averaging Nuve thermostats daily installations over the past 14 days.
              If, for example, the average rate is 10 and there are 150 thermostats in stock, the widget shows
              &apos;Sufficient for 15 days,&apos; indicating the estimated stock duration based on historical rates.
            </SufficientTooltip>
          </MainLabel>
          <ValueWrapper>
            {widgetData.sufficientDays}
            <ValueNote> Days</ValueNote>
          </ValueWrapper>
        </MainValueWrapper>
        <WidgetFooter>
          <WidgetFooterItem>
            <FooterNote $color="#3B82F6">
              <Tooltip id={uuidv4()} text="In Inventory">
                Number of available thermostats in storage
              </Tooltip>
            </FooterNote>
            <ValueWrapper>{widgetData.inventoryDevices}</ValueWrapper>
          </WidgetFooterItem>
          <WidgetFooterItem>
            <FooterNote $color="#BFDBFE">
              <Tooltip id={uuidv4()} text="Ordered">
                Number of ordered thermostats
              </Tooltip>
            </FooterNote>
            <ValueWrapper>{widgetData.orderedDevices}</ValueWrapper>
          </WidgetFooterItem>
        </WidgetFooter>
      </WidgetContent>
    </WidgetsLayout>
  )
}
